import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Automacao from '../../assets/images/automacao.png';
import IphoneBotChat from '../../assets/images/iphone3.png';
import { MessageCircle, Search, Users, FileText } from 'lucide-react';

const LandingPage = () => {
  return (
    <div className="bg-gray-50 text-gray-800 font-sans">
      {/* Hero Section */}
      <section className="bg-gradient-blue-green text-white py-20 px-6 text-center">

        <motion.h1
          className="text-4xl md:text-6xl font-bold mb-4"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="mt-8 text-4xl md:text-6xl font-bold leading-tight tracking-tight">
            Automatize seu atendimento.<br />Venda mais.<br />Atenda melhor.
          </h1>
        </motion.h1>
        <motion.p
          className="text-lg md:text-xl mb-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          Com o Atende24h, seus clientes têm resposta em qualquer hora do dia — via WhatsApp, Instagram e Web.
        </motion.p>
        <motion.button
          className="bg-white text-blue-600 font-semibold px-6 py-3 rounded-full hover:bg-gray-200 transition"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Quero automatizar meu atendimento
        </motion.button>
      </section>

      {/* Sobre */}
      <section className="py-16 px-4 md:px-6 max-w-5xl mx-auto text-center">
        <motion.h2
          className="text-3xl font-bold mb-4"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          O que é o Atende24h?
        </motion.h2>
        <p className="text-lg text-gray-700">O Atende24h é uma solução de atendimento automatizado e inteligente que integra canais como WhatsApp, Instagram e site, com IA e transferência automática para atendentes humanos.</p>
      </section>

      {/* Benefícios */}
      <section className="bg-white py-16 px-4 md:px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 text-center">
          {[
            { icon: '💬', text: 'Atendimento 24h por dia' },
            { icon: '⚡', text: 'Respostas rápidas com IA' },
            { icon: '🤖', text: 'Chatbots personalizados' },
            { icon: '🔄', text: 'Transferência para atendente' },
            { icon: '📊', text: 'Relatórios e histórico' },
            { icon: '🔌', text: 'Integração com CRM via N8N' },
          ].map((item, index) => (
            <motion.div
              key={index}
              className="shadow-lg p-6 rounded-lg"
              whileHover={{ scale: 1.05 }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="text-4xl mb-2">{item.icon}</div>
              <p className="text-lg font-semibold text-gray-700">{item.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="flex flex-col md:flex-row items-center justify-between px-8 py-12 bg-gradient-to-r from-blue-200 via-indigo-100 to-white">
        {/* Imagem à esquerda */}
        <div className="md:w-1/2 mb-10 md:mb-0 flex justify-center">
          <div className="relative max-w-sm">
            <img
              src={Automacao} // substitua pelo seu caminho de imagem
              alt="Photo Editor Preview"
              className="rounded-lg shadow-xl"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-purple-400/30 to-transparent rounded-lg" />
          </div>
        </div>

        {/* Texto à direita */}
        <div className="md:w-1/2 text-left">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Sistema de Atendimento Unificado (SaaS)
          </h2>
          <h3 className="text-2xl md:text-2xl font-bold text-gray-600 mb-4">
            Inteligência Artificial e integração via WhatsApp
          </h3>
          <h4 className="text-1xl md:text-1xl font-bold text-gray-600 mb-4">
            Principais Vantagens da Solução:
          </h4>
          <p className="text-gray-700 mb-4">
            ✅ Atendimento 24h por dia <br />
            ✅ Encaminhamento automático por assunto <br />
            ✅ Integração com o WhatsApp (sem precisar instalar app adicional) <br />
            ✅ Acompanhamento por dashboard dos atendimentos <br />
            ✅ Redução de filas presenciais e ligações telefônicas <br />
            ✅ Agente de IA que entende a linguagem natural<br />
            ✅ Controle de desempenho dos atendentes por setor <br />
            ✅ Histórico de conversas centralizado e arquivado <br />
            ✅ Notificações automáticas por mensagens (ex: vencimentos, convocações, etc.) <br />
            ✅ Maior transparência e eficiência na gestão <br />
          </p>

          <button className="bg-gradient-blue-green text-white font-semibold px-6 py-3 rounded-md shadow">
            Contrate já
          </button>
        </div>
      </section>

      {/* Como funciona */}
      <section className="py-6 px-4 md:px-6 bg-gray-100">

        <div className="flex flex-col-reverse md:flex-row items-center justify-center gap-12 max-w-6xl mx-auto">
          {/* Lista de etapas */}
          <div className="md:w-1/2 space-y-6">
            <motion.h2
              className="text-4xl font-bold mb-12 text-gray-800"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Como Funciona
            </motion.h2>
            <div className="flex items-start gap-4">
              <MessageCircle className="text-indigo-600 w-6 h-6 mt-1" />
              <p className="text-lg text-gray-700 leading-relaxed">
                <span className="font-semibold text-indigo-800">1. Envio da mensagem:</span> o cliente inicia o contato pelo WhatsApp, Instagram ou Webchat.
              </p>
            </div>
            <div className="flex items-start gap-4">
              <Search className="text-indigo-600 w-6 h-6 mt-1" />
              <p className="text-lg text-gray-700 leading-relaxed">
                <span className="font-semibold text-indigo-800">2. O bot analisa a necessidade:</span> com inteligência automatizada, ele responde ao cliente.
              </p>
            </div>
            <div className="flex items-start gap-4">
              <Users className="text-indigo-600 w-6 h-6 mt-1" />
              <p className="text-lg text-gray-700 leading-relaxed">
                <span className="font-semibold text-indigo-800">3. Encaminhamento humano:</span> se necessário, o atendimento é transferido para um agente.
              </p>
            </div>
            <div className="flex items-start gap-4">
              <FileText className="text-indigo-600 w-6 h-6 mt-1" />
              <p className="text-lg text-gray-700 leading-relaxed">
                <span className="font-semibold text-indigo-800">4. Registro:</span> todo o histórico fica salvo para análise e melhoria do atendimento.
              </p>
            </div>
          </div>

          {/* Imagem do iPhone */}
          <div className="md:w-1/3 flex justify-center">
            <img
              src={IphoneBotChat}
              alt="Conversa no WhatsApp com o bot"
              className="w-[280px] md:w-[180px] lg:w-[200px]"
            />
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 px-4 md:px-6 bg-blue-500 text-white text-center">
        <motion.h2
          className="text-4xl font-bold mb-4"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Pronto para automatizar seu atendimento?
        </motion.h2>
        <p className="text-lg mb-6">Experimente o Atende24h e descubra como transformar seu atendimento.</p>
        <motion.button
          className="bg-white text-blue-600 font-semibold px-6 py-3 rounded-full hover:bg-gray-200 transition"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Solicitar Demonstração
        </motion.button>
      </section>

      {/* Rodapé */}
      <footer className="bg-gray-800 text-white py-8 text-center">
        <p>&copy; {new Date().getFullYear()} Atende24h. Todos os direitos reservados.</p>
        <p className="text-sm mt-2">WhatsApp | Instagram </p>
        <p className="mb-4">
          <Link
            to="/politica"
            className="text-white hover:text-blue-300"
          >
            Política de Privacidade
          </Link>   {' | '}
          <Link
            to="/uso"
            className="text-white hover:text-blue-300"
          >
            Termos de Uso
          </Link>         
        </p>

      </footer>
    </div>
  );
};

export default LandingPage;
